import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, LayoutGrid, Clock, Trash2, Edit2, Image, Check, Loader2, Settings, LogOut, Users, Ticket, ChevronDown, Film, Pencil, Sparkle, HardDrive, X, Monitor, CreditCard, Radio, Sun, Moon, Boxes } from 'lucide-react';
import QuickTooltip from './components/QuickTooltip';
import { useTheme } from './hooks/useTheme';
import { io } from 'socket.io-client';
import * as C from './constants';
import api from './api'; // 🆕 引入 API 工具
import CineartLogo from './components/CineartLogo';
import CoverCropperModal from './components/CoverCropperModal';
import ImagePickerModal from './components/ImagePickerModal';
import ConfirmModal from './components/ConfirmModal';
import ApiConfigModal from './components/ApiConfigModal';
import ApiProviderModal from './components/ApiProviderModal';
import AdminPanel from './components/AdminPanel'; // 🆕 管理员面板
import MaterialLibraryAdminPanel from './components/MaterialLibrary/MaterialLibraryAdminPanel'; // 🎨 素材库管理（环艺学姐）
import UserSettingsModal from './components/UserSettingsModal'; // 🆕 用户设置
import InviteCodeModal from './components/InviteCodeModal'; // 🆕 邀请码管理
import CreditLogsModal from './components/CreditLogsModal'; // 💰 积分明细
import RechargeModal from './components/RechargeModal'; // 💳 弹窗（教育版构建时换成空壳 stub）
import CrewInviteModal from './components/CrewInviteModal'; // 🎬 剧组邀请弹窗
import CrewCreditManageModal from './components/CrewCreditManageModal'; // 🎬 剧组积分管理
import LoginBrandingEditor from './components/LoginBrandingEditor'; // 🎨 登录界面配置
import DistributionPanel from './components/DistributionPanel'; // 🎫 放号管理
import UserAvatar from './components/UserAvatar'; // 🎨 统一头像组件
import { useAuth } from './contexts/AuthContext';
import { useProjectLoading } from './contexts/ProjectLoadingContext'; // 🆕 全局项目切换加载状态
import localCacheService from './services/localCacheService';
import dashboardCacheService from './services/dashboardCacheService';

import { IconFilm } from './editorUtils';
import { IS_EDU, IS_HUANSHE } from './config/edition'; // 🎓 教育版隐藏入口、改走算力券 | 🎨 环艺学姐专属功能
import VoucherRedeemModal from './components/VoucherRedeemModal'; // 🎓 学生兑换算力券
import VoucherPanel from './components/VoucherPanel'; // 🎓 老师批量发放算力券

// ════════════════════════════════════════════════════════════════════
// 📡 项目状态指示器 — 桌面卡片右下角的旋转/对勾图标
//
// 数据来源（两条路径，互为兜底）：
//   1. loadProjects() API 拉取 → 从 DB 的 pending_tasks 和 has_unseen_completed 字段获取
//   2. WebSocket project-status-changed 事件 → 使用 pendingTasks 绝对值实时更新
//
// 为什么用绝对值而非 pendingDelta：
//   DashboardPage 的 WebSocket 可能在服务端发出 +1 事件之后才连接上（用户从编辑器快速点 Logo 回桌面），
//   如果用 delta 增量，就会丢失那个 +1，导致 pendingTasks 始终为 0（不显示旋转）。
//   改用服务端从 DB 查询的绝对值后，无论事件是否丢失，收到任一后续事件都能纠正为正确值。
//
// 兜底机制：socket connect 时重新 loadProjects()，确保即使所有事件都丢失也能拿到正确状态。
//
// ⚠️ 修改注意事项：
//   - 不要改为使用 pendingDelta，会导致页面切换时丢事件
//   - 不要删除 socket connect 时的 loadProjects 调用
//   - EditorPage 抽屉的 ProjectStatusIndicator 在 AssetDrawer.js 中，用的是 pendingDelta（因为初始值从 API 获取准确）
// ════════════════════════════════════════════════════════════════════
const ProjectStatusIndicator = ({ pendingTasks, hasUnseenCompleted }) => {
    if (pendingTasks > 0) {
        // 生成中：旋转圈 + 数字
        return (
            <div style={{
                position: 'absolute', bottom: 8, right: 8,
                background: 'rgba(59, 130, 246, 0.9)', borderRadius: '50%',
                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.5)'
            }}>
                <Loader2 size={16} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
                {pendingTasks > 1 && (
                    <span style={{
                        position: 'absolute', top: -4, right: -4,
                        background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 'bold',
                        borderRadius: '50%', width: 16, height: 16,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>{pendingTasks}</span>
                )}
            </div>
        );
    }
    
    if (hasUnseenCompleted) {
        // 已完成：绿色对勾
        return (
            <div style={{
                position: 'absolute', bottom: 8, right: 8,
                background: 'rgba(34, 197, 94, 0.9)', borderRadius: '50%',
                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.5)'
            }}>
                <Check size={16} color="#fff" strokeWidth={3} />
            </div>
        );
    }
    
    return null;
};

// 🆕 带 shimmer 加载的图片组件 — 图片未加载完前显示骨架屏
//
// 为什么要在 src 变化时复位 loaded？
//   首页缩略图会经历 src 两次切换：
//     ① 初次渲染：src = 远程 thumbnail URL
//     ② 三层缓存命中：src = 本地 blob URL
//   如果不复位，第 ② 次切换时 <img> 重新加载、loaded 仍为 true，
//   会在 blob URL 真正解析完成前露出"已显示"的状态，看到一帧空白。
const ShimmerImg = ({ src, alt = "", style = {}, ...rest }) => {
    const [loaded, setLoaded] = useState(false);
    const lastSrcRef = useRef(src);
    if (lastSrcRef.current !== src) {
        lastSrcRef.current = src;
        if (loaded) setLoaded(false);
    }
    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {!loaded && <div className="shimmer-wrapper" style={{ position: 'absolute', inset: 0 }} />}
            <img
                src={src} alt={alt}
                style={{ ...style, opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
                onLoad={() => setLoaded(true)}
                {...rest}
            />
        </div>
    );
};

// 仪表盘页面
export default function DashboardPage() {
    const navigate = useNavigate();
    const { user, logout, hasPermission } = useAuth(); // 🆕 获取用户信息和登出方法
    const { stopLoading } = useProjectLoading(); // 🆕 停止全局加载反馈
    const [theme, toggleTheme] = useTheme(); // 🎨 深浅色主题切换
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const inputRef = useRef(null);
    
    // 封面设置相关状态
    const [coverProject, setCoverProject] = useState(null); // 当前要设置封面的项目
    const [projectImages, setProjectImages] = useState([]); // 项目中的图片列表
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [cropImageUrl, setCropImageUrl] = useState(null); // 要裁剪的图片
    
    // 删除确认弹窗状态
    const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }
    const [deleteCrewConfirm, setDeleteCrewConfirm] = useState(null); // 🎬 删除影片确认 { id, name, projectCount, memberCount }
    const [switchingCrewId, setSwitchingCrewId] = useState(null); // 🎬 正在切换的影片 id（服务器慢时给出"切换中"反馈）
    const [creatingProject, setCreatingProject] = useState(false); // 新建舞台加载态
    const [loadingProjectId, setLoadingProjectId] = useState(null); // 正在打开的项目 id（低速网络/低配电脑立即反馈）
    
    // API 配置弹窗状态
    const [showApiConfig, setShowApiConfig] = useState(false);
    const [showApiProvider, setShowApiProvider] = useState(false);
    
    // 🆕 管理员面板状态
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [showMaterialLibraryAdmin, setShowMaterialLibraryAdmin] = useState(false); // 🎨 素材库管理（环艺学姐）
    
    // 🆕 邀请码弹窗状态
    const [showInviteCode, setShowInviteCode] = useState(false);
    
    // 🎫 放号管理弹窗状态
    const [showDistribution, setShowDistribution] = useState(false);
    
    // 🆕 用户设置弹窗状态
    const [showUserSettings, setShowUserSettings] = useState(false);
    
    // 🎨 登录界面配置弹窗状态
    const [showLoginBranding, setShowLoginBranding] = useState(false);
    
    // 💰 用户积分状态
    const [userCredits, setUserCredits] = useState(0);
    const [showCreditLogs, setShowCreditLogs] = useState(false);
    const [showRecharge, setShowRecharge] = useState(false);
    // 🎓 教育版：换成算力券（学生兑换 / 老师发放）
    const [showVoucherRedeem, setShowVoucherRedeem] = useState(false);
    const [showVoucherPanel, setShowVoucherPanel] = useState(false);
    
    // � 本地缓存失效横幅状态
    const [cacheInvalid, setCacheInvalid] = useState(false);
    const [cacheReconfiguring, setCacheReconfiguring] = useState(false);
    const [showCacheConfirm, setShowCacheConfirm] = useState(false);

    // �🎬 剧组系统状态
    const [crews, setCrews] = useState([]); // 用户参与的所有剧组
    const [currentCrew, setCurrentCrew] = useState(null); // 当前选中的剧组
    const [crewMembers, setCrewMembers] = useState([]); // 当前剧组成员
    const [showCrewDropdown, setShowCrewDropdown] = useState(false); // 剧组下拉框
    const [editingCrewName, setEditingCrewName] = useState(false); // 是否正在编辑剧组名
    const [crewNameInput, setCrewNameInput] = useState(''); // 剧组名输入
    const [showCrewInvite, setShowCrewInvite] = useState(false); // 邀请弹窗
    const [showCrewCreditManage, setShowCrewCreditManage] = useState(false); // 🎬 剧组积分管理弹窗
    /**
     * 🖼️ 海报相关 State
     *
     * 影片海报（poster）与项目封面（cover）是两个独立概念：
     *   - 封面（cover）：单个舞台（project）的缩略图，显示在舞台卡片上
     *   - 海报（poster）：整部影片（crew）的宣传海报，固定浮动在仪表盘右侧
     *
     * 裁剪弹窗（CoverCropperModal）被两者共用：
     *   - posterMode=false → 封面模式，比例 16:9，回调 handleCoverSave
     *   - posterMode=true  → 海报模式，比例 9:16，回调 handlePosterSave
     *
     * ⚠️ 裁剪比例：海报固定为 9:16（竖屏），封面固定为 16:9（横屏）
     *    修改时需同步更新以下三处：
     *    1. DashboardPage.js CoverCropperModal 的 aspectRatio prop（posterMode 分支）
     *    2. EditorPage.js CoverCropperModal 的 aspectRatio prop（posterCropUrl 分支）
     *    3. DashboardPage.js 浮动海报 div 的 aspectRatio style
     */
    const [posterMode, setPosterMode] = useState(false); // true = 正在设置海报，false = 正在设置封面
    const [posterHidden, setPosterHidden] = useState(false); // 用户手动关闭了浮动海报（切换影片时重置）
    const [posterTop, setPosterTop] = useState(232); // 浮动海报的 top 值，与内容网格顶部对齐（由 useEffect 动态计算）
    const crewNameInputRef = useRef(null);
    const crewDropdownRef = useRef(null);
    const gridRef = useRef(null); // 用于对齐浮动海报
    // 🛡️ 防止 user?.id 变化后旧请求写入新用户数据（H8 竞态修复）
    const effectAliveRef = useRef(true);

    // 自动聚焦剧组重命名输入框
    useEffect(() => {
        if (editingCrewName && crewNameInputRef.current) {
            crewNameInputRef.current.focus();
            crewNameInputRef.current.select();
        }
    }, [editingCrewName]);

    // ────────────────────────────────────────────────────────────────
    // 加载舞台列表（stale-while-revalidate 模式）
    // ────────────────────────────────────────────────────────────────
    //
    // 双轨：
    //   ① 立即从 IndexedDB 取上次的快照渲染（< 50ms，0 网络）
    //   ② 同时后台拉 /api/projects，到了再覆盖 state + 重写缓存
    //
    // 见 [services/dashboardCacheService.js] 的整体说明。
    //
    // ⚠️ 拒绝快照覆盖正在加载中的 state：
    //   如果用户极快地切换账号/影片，旧请求的快照可能在新请求之后回来。
    //   prev.length ? prev : snap.data 保证只在 state 还为空时才贴快照。
    // ────────────────────────────────────────────────────────────────
    const loadProjects = useCallback(() => {
        setLoading(true);
        const uid = user?.id;

        // ⏱️ 兜底：如果 2 秒后仍在加载，强制关闭 loading（避免卡死）
        const forceStopTimer = setTimeout(() => {
            if (effectAliveRef.current) {
                console.warn('⚠️ loadProjects 超时，强制关闭 loading');
                setLoading(false);
            }
        }, 2000);

        if (uid) {
            dashboardCacheService.get(dashboardCacheService.keys.projects(uid)).then(snap => {
                if (!effectAliveRef.current || !snap?.data) return;
                setProjects(prev => prev.length ? prev : snap.data);
                setLoading(false);
                clearTimeout(forceStopTimer);
            }).catch(() => {});
        }
        api.get('/api/projects?limit=100') // 一次加载最多 100 个舞台
            .then(data => {
                if (!effectAliveRef.current) return; // 🛡️ H8: 防止旧请求覆盖新用户数据
                // 兼容新版分页格式 { projects, total } 和旧版数组格式
                const list = Array.isArray(data) ? data : (data.projects || []);
                setProjects(list);
                // 写回缓存供下次秒开。put 内部会自动剔除 localThumbUrl 等运行时字段。
                if (uid) dashboardCacheService.put(dashboardCacheService.keys.projects(uid), list);
            })
            .catch(err => console.error("Load Projects Failed", err))
            .finally(() => {
                clearTimeout(forceStopTimer);
                if (effectAliveRef.current) setLoading(false);
            });
    }, [user?.id]);
    
    // 💰 加载用户积分（含超级管理员——超管已纳入正常扣费，2026-06-16）
    const loadCredits = useCallback(() => {
        api.get('/api/user/credits')
            .then(data => {
                if (effectAliveRef.current) setUserCredits(data.credits || 0); // 🛡️ H8
            })
            .catch(err => console.error("Load Credits Failed", err));
    }, [user?.role]);
    
    // ────────────────────────────────────────────────────────────────
    // 加载影片列表（同样 stale-while-revalidate）
    // ────────────────────────────────────────────────────────────────
    //
    // 比 loadProjects 多一步：cached 里还要包含 currentCrewId，否则
    // 用快照渲染时下拉框会显示空。
    // ────────────────────────────────────────────────────────────────
    const loadCrews = useCallback(async () => {
        const uid = user?.id;
        if (uid) {
            try {
                const snap = await dashboardCacheService.get(dashboardCacheService.keys.crews(uid));
                if (effectAliveRef.current && snap?.data) {
                    const cached = snap.data;
                    setCrews(prev => prev.length ? prev : (cached.crews || []));
                    if (cached.currentCrewId && cached.crews?.length > 0) {
                        const current = cached.crews.find(c => c.id === cached.currentCrewId);
                        if (current) {
                            setCurrentCrew(prev => prev || current);
                            setCrewNameInput(prev => prev || current.name);
                        }
                    }
                }
            } catch (_) {}
        }
        try {
            const data = await api.get('/api/crews');
            if (!effectAliveRef.current) return; // 🛡️ H8: 防止旧请求覆盖新用户数据
            setCrews(data.crews || []);

            // 设置当前剧组
            if (data.currentCrewId && data.crews?.length > 0) {
                const current = data.crews.find(c => c.id === data.currentCrewId);
                if (current) {
                    setCurrentCrew(current);
                    setCrewNameInput(current.name);
                }
            } else if (data.crews?.length > 0) {
                setCurrentCrew(data.crews[0]);
                setCrewNameInput(data.crews[0].name);
            }
            if (uid) {
                dashboardCacheService.put(dashboardCacheService.keys.crews(uid), {
                    crews: data.crews || [],
                    currentCrewId: data.currentCrewId,
                });
            }
        } catch (err) {
            console.error("Load Crews Failed", err);
        }
    }, [user?.id]);
    
    // 🎬 加载当前剧组成员
    const loadCrewMembers = async (crewId) => {
        if (!crewId) return;
        try {
            const data = await api.get(`/api/crews/${crewId}/members`);
            setCrewMembers(data.members || []);
        } catch (err) {
            console.error("Load Crew Members Failed", err);
        }
    };
    
    // 🎬 切换剧组
    const handleSwitchCrew = async (crew) => {
        if (switchingCrewId) return; // 防止切换过程中重复点击
        if (currentCrew?.id === crew.id) { setShowCrewDropdown(false); return; } // 点击当前影片直接收起
        setSwitchingCrewId(crew.id); // 立即给被点影片显示"切换中"旋转图标
        try {
            await api.put(`/api/crews/${crew.id}/switch`);
            setCurrentCrew(crew);
            setPosterHidden(false); // 切换剧组时重新显示海报
            setCrewNameInput(crew.name);
            setShowCrewDropdown(false);
            loadProjects(); // 重新加载项目（按新剧组过滤）
            loadCrewMembers(crew.id);
        } catch (err) {
            console.error("Switch Crew Failed", err);
        } finally {
            setSwitchingCrewId(null);
        }
    };
    
    // 🎬 创建新影片
    const handleCreateCrew = async () => {
        try {
            const data = await api.post('/api/crews', { name: '新项目' });
            if (data.success) {
                // 🔧 统一role字段（后端返回myRole，前端统一为role）
                const crew = { ...data.crew, role: data.crew.myRole || data.crew.role };
                setCrews(prev => [...prev, crew]);
                setCurrentCrew(crew);
                setCrewNameInput(crew.name);
                setShowCrewDropdown(false);
                loadProjects();
                loadCrewMembers(crew.id);
            }
        } catch (err) {
            console.error("Create Crew Failed", err);
        }
    };
    
    // 🎬 保存剧组名称
    const handleSaveCrewName = async () => {
        if (!currentCrew || !crewNameInput.trim()) {
            setEditingCrewName(false);
            return;
        }
        if (crewNameInput === currentCrew.name) {
            setEditingCrewName(false);
            return;
        }
        
        const oldName = currentCrew.name;
        const newName = crewNameInput;
        
        try {
            await api.put(`/api/crews/${currentCrew.id}`, { name: newName });
            setCurrentCrew(prev => ({ ...prev, name: newName }));
            setCrews(prev => prev.map(c => c.id === currentCrew.id ? { ...c, name: newName } : c));
            setEditingCrewName(false);

            // 📂 迁移本地缓存文件夹
            localCacheService.migrateCacheFolder(currentCrew.id, oldName, newName)
              .catch(err => console.warn('缓存文件夹迁移提示:', err));
                
        } catch (err) {
            console.error("Update Crew Name Failed", err);
        }
    };

    // 🎬 删除影片（打开确认弹窗）
    const handleDeleteCrew = async (e, crew) => {
        e.stopPropagation();
        
        // 获取该影片的统计信息
        try {
            // 获取该影片下的舞台数量
            const resp = await api.get('/api/projects');
            const allProjects = Array.isArray(resp) ? resp : (resp.projects || []);
            const crewProjects = allProjects.filter(p => p.crewId === crew.id);
            
            // 获取目标影片的成员数量（而非当前选中影片）
            let targetMemberCount = 0;
            try {
                const members = await api.get(`/api/crews/${crew.id}/members`);
                targetMemberCount = Array.isArray(members) ? members.length : 0;
            } catch (e) {
                // 获取失败不阻断流程
            }

            setDeleteCrewConfirm({
                id: crew.id,
                name: crew.name,
                projectCount: crewProjects.length,
                memberCount: targetMemberCount
            });
        } catch (err) {
            console.error('获取影片统计信息失败:', err);
            // 即使获取失败也允许删除
            setDeleteCrewConfirm({
                id: crew.id,
                name: crew.name,
                projectCount: 0,
                memberCount: 0
            });
        }
    };

    // 🎬 确认删除影片
    const confirmDeleteCrew = async () => {
        if (!deleteCrewConfirm) return;
        
        try {
            await api.delete(`/api/crews/${deleteCrewConfirm.id}`);
            
            // 🗑️ 删除该影片的本地缓存文件夹
            localCacheService.deleteCrewCacheFolder(deleteCrewConfirm.id, deleteCrewConfirm.name).catch(() => {});
            
            // 从列表中移除该影片，并用函数式更新获取最新 crews 列表
            setCrews(prev => {
                const remaining = prev.filter(c => c.id !== deleteCrewConfirm.id);
                
                // 如果删除的是当前影片，切换到其他影片
                if (currentCrew?.id === deleteCrewConfirm.id) {
                    if (remaining.length > 0) {
                        setCurrentCrew(remaining[0]);
                        setCrewNameInput(remaining[0].name);
                        loadProjects();
                        loadCrewMembers(remaining[0].id);
                    } else {
                        setCurrentCrew(null);
                        setProjects([]);
                    }
                }
                
                return remaining;
            });
            
            setDeleteCrewConfirm(null);
        } catch (err) {
            console.error('删除影片失败:', err);
            alert(err.response?.data?.error || '删除影片失败');
        }
    };

    useEffect(() => {
        // 🛡️ H8: 标记当前 effect 存活，用于防止 user?.id 切换时旧请求写入新数据
        effectAliveRef.current = true;

        // 🆕 从编辑器返回时，关闭全局加载反馈
        stopLoading();

        loadCrews(); // 🎬 加载剧组列表
        loadProjects();
        loadCredits(); // 💰 加载积分

        // 🆕 验证本地缓存配置是否有效（换电脑/文件夹删除/新用户场景）
        if (localCacheService.isFileSystemAccessSupported()) {
            localCacheService.validateCacheConfig().then(result => {
                if (!effectAliveRef.current) return; // 🛡️ H8
                if (!result.valid) {
                    if (result.notConfigured) {
                        console.log('ℹ️ 本地缓存尚未配置（新用户），提示配置');
                        setCacheInvalid('not-configured');
                    } else if (result.needReauth) {
                        console.log('ℹ️ 本地缓存权限过期，可一键恢复');
                        setCacheInvalid('need-reauth');
                    } else if (result.needReselect) {
                        console.warn('⚠️ 本地缓存文件夹不可用，需要重新配置');
                        setCacheInvalid('invalid');
                    }
                } else {
                    setCacheInvalid(false);
                }
            }).catch(e => console.warn('缓存验证出错:', e));
        }
        
        // 📡 建立 WebSocket 连接，监听项目状态变化
        // DashboardPage 只加入 private-${userId} 房间（服务端 connection 时自动加入），
        // 不加入任何 project-${pid} 房间，因此收到的每个事件都是唯一的，不需要去重。
        const wsToken = api.getToken ? api.getToken() : localStorage.getItem('cineart_token');
        const socket = io(C.API_BASE_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            auth: { token: wsToken },
        });
        
        socket.on('connect', () => {
            console.log('🔌 DashboardPage WebSocket 已连接');
            // 🔄 延迟兜底：5 秒后检查是否需要重新拉取（避免与首次 loadProjects 冲突）
            //   用户在编辑器点击生成 → 后端还没来得及处理 → 用户快速点 Logo 回桌面
            //   → 首次 loadProjects 返回 pendingTasks=0 → WebSocket +1 事件可能在连接前已发出
            //   → 结果：桌面看不到旋转。延迟重新拉取可纠正此问题。
            setTimeout(() => {
                if (!effectAliveRef.current) return;
                api.get('/api/projects?limit=100')
                    .then(data => {
                        if (!effectAliveRef.current) return; // 🛡️ H8
                        const list = Array.isArray(data) ? data : (data.projects || []);
                        setProjects(list);
                    })
                    .catch(() => {});
            }, 5000); // 5 秒后才补偿，避免与首次加载冲突
        });
        
        // 📡 监听项目状态变化（使用绝对值 pendingTasks，不用 pendingDelta）
        // 原因：DashboardPage 可能在 delta 事件发出后才连接 WebSocket → 丢失事件 → 计数错误
        // 绝对值始终反映 DB 的真实状态，任何一次事件都能纠正偏差
        socket.on('project-status-changed', (payload) => {
            const { projectId, pendingTasks: absolutePendingTasks, hasUnseenCompleted } = payload;

            const numProjectId = typeof projectId === 'string' ? parseInt(projectId) : projectId;

            setProjects(prev => prev.map(p => {
                if (p.id !== numProjectId) return p;

                const updates = {};

                if (absolutePendingTasks !== undefined) {
                    updates.pendingTasks = absolutePendingTasks;
                }

                if (hasUnseenCompleted !== undefined) {
                    updates.hasUnseenCompleted = hasUnseenCompleted;
                }

                return Object.keys(updates).length ? { ...p, ...updates } : p;
            }));
        });
        
        // 💰 监听积分变化
        socket.on('credits-updated', (payload) => {
            if (payload.userId === user?.id) {
                setUserCredits(payload.credits);
            }
        });
        
        return () => {
            effectAliveRef.current = false; // 🛡️ H8: 标记旧 effect 失效
            socket.disconnect();
            console.log('🔌 DashboardPage WebSocket 已断开');
        };
    }, [loadCrews, loadProjects, loadCredits, user?.id, stopLoading]);

    // ════════════════════════════════════════════════════════════════
    // 仪表盘三层缓存：列表快照 + 缩略图 blob
    // ════════════════════════════════════════════════════════════════
    //
    // 这一段是首页"刷新半天才出来"问题的核心修复，由四个 effect 组成：
    //
    //   1) projects 持久化       → 任何 state 变化（删除/重命名/WebSocket）后
    //                              debounce 300ms 写入 IndexedDB 列表快照
    //   2) crews 持久化          → 同上，crews + currentCrewId
    //   3) 项目封面缩略图预热    → 把 thumbnail URL 喂给 localCacheService，
    //                              结果写到 p.localThumbUrl，UI 用本地 blob 显示
    //   4) 影片海报预热          → 同 ③，结果写到 posterLocalUrl 局部 state
    //
    // 数据流：
    //   服务器 thumbnail URL  ──[localCacheService.getLocalUrlOrFetch]──┐
    //                                                                  │
    //                              ┌── L0 内存 Map  ──┐                │
    //                              │── L1 IndexedDB ─┤                 │
    //                              │── L2 本地文件夹 │  全 miss 才下载 │
    //                              └─────────────────┘                 │
    //                                       │                          │
    //                              blob URL ◄──────────────────────────┘
    //                                       ▼
    //                          setProjects(p.localThumbUrl = blob URL)
    //                                       ▼
    //                          <ShimmerImg src={p.localThumbUrl || p.thumbnail}>
    //
    // 详细架构见 [文件缓存与上传架构说明.md] 第二节。
    // ════════════════════════════════════════════════════════════════

    // ──── ① projects 列表持久化（debounce 300ms）────
    // 触发场景：删除舞台、改名、改封面、WebSocket pendingTasks 更新等。
    // 写入时机被 debounce 折叠成最多 300ms 一次，避免 WebSocket 高频事件
    // 时反复写 IndexedDB。
    useEffect(() => {
        if (!user?.id || !projects.length) return;
        const t = setTimeout(() => {
            dashboardCacheService.put(dashboardCacheService.keys.projects(user.id), projects);
        }, 300);
        return () => clearTimeout(t);
    }, [projects, user?.id]);

    // ──── ② crews 列表持久化（debounce 300ms）────
    // 包含 currentCrewId 一起写，否则下次 stale-while-revalidate 渲染时
    // 当前选中的影片会丢失。
    useEffect(() => {
        if (!user?.id || !crews.length) return;
        const t = setTimeout(() => {
            dashboardCacheService.put(dashboardCacheService.keys.crews(user.id), {
                crews,
                currentCrewId: currentCrew?.id,
            });
        }, 300);
        return () => clearTimeout(t);
    }, [crews, currentCrew?.id, user?.id]);

    // ──── ③ 项目封面缩略图：远程 URL → 本地 blob URL ────
    //
    // 6 并发的 worker pool，逐个把 thumbnail 喂给 getLocalUrlOrFetch：
    //   - 命中三层缓存 → 立即拿到 blob URL，写到 p.localThumbUrl，UI 切显
    //   - 全 miss → 远程下载并回填 L1/L2，下次秒命中
    //
    // thumbnailKey 是依赖优化：
    //   原本 [projects, currentCrew?.name] 会让 WebSocket 状态更新（如
    //   pendingTasks 数字跳）也触发 effect 重跑。改用 (id|thumbnail) 拼成
    //   的字符串作 key，只有真正的项目集合 / 封面 URL 变化时才重跑。
    //
    // 写回 localThumbUrl 时再次校验 thumbnail：
    //   防止用户极快换封面时旧请求把过期 blob 写到新封面的项目上。
    //
    // ⏱️ 性能优化：使用 requestIdleCallback 延迟预热，不阻塞首屏渲染
    const thumbnailKey = projects.map(p => `${p.id}|${p.thumbnail || ''}`).join(',');
    useEffect(() => {
        if (!projects.length) return;
        let cancelled = false;
        const targets = projects.filter(p => p.thumbnail && !p.localThumbUrl);
        if (targets.length === 0) return;

        // 延迟 500ms 后开始预热，避免阻塞首屏
        const delayTimer = setTimeout(() => {
            if (cancelled) return;

            const CONCURRENCY = 6;
            const queue = targets.slice();
            const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
                while (!cancelled && queue.length) {
                    const p = queue.shift();
                    try {
                        const r = await localCacheService.getLocalUrlOrFetch(
                            p.thumbnail, p.crewId, currentCrew?.name, 'image'
                        );
                        if (cancelled || !r?.url) continue;
                        setProjects(prev => prev.map(x => {
                            if (x.id !== p.id) return x;
                            if (x.thumbnail !== p.thumbnail) return x; // 旧请求 → 丢弃
                            return { ...x, localThumbUrl: r.url };
                        }));
                    } catch (e) {
                        // 远程失败保留原 URL，ShimmerImg 会用 p.thumbnail 兜底
                    }
                }
            });
            Promise.all(workers).catch(() => {});
        }, 500); // 延迟 500ms

        return () => {
            cancelled = true;
            clearTimeout(delayTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thumbnailKey, currentCrew?.name]);

    // ──── ④ 影片海报缩略图（独立单图）────
    // 海报只有一张，不用 worker pool。
    // 切换影片时旧 effect 的 cancelled = true，不会污染新影片的 posterLocalUrl。
    const [posterLocalUrl, setPosterLocalUrl] = useState(null);
    useEffect(() => {
        if (!currentCrew?.poster) { setPosterLocalUrl(null); return; }
        let cancelled = false;
        localCacheService.getLocalUrlOrFetch(
            currentCrew.poster, currentCrew.id, currentCrew.name, 'image'
        ).then(r => {
            if (!cancelled && r?.url) setPosterLocalUrl(r.url);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [currentCrew?.id, currentCrew?.name, currentCrew?.poster]);

    // � 持续监控本地缓存有效性：页面切回前台 / 其他组件重配后同步横幅
    useEffect(() => {
        if (!localCacheService.isFileSystemAccessSupported()) return;

        const checkCache = async () => {
            try {
                const result = await localCacheService.validateCacheConfig();
                if (!result.valid) {
                    if (result.notConfigured) {
                        setCacheInvalid('not-configured');
                    } else if (result.needReauth) {
                        setCacheInvalid('need-reauth');
                    } else if (result.needReselect) {
                        setCacheInvalid('invalid');
                    }
                } else {
                    setCacheInvalid(false);
                }
            } catch (e) {
                console.warn('缓存验证出错:', e);
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') checkCache();
        };

        const handleCacheChange = (e) => {
            if (e.detail?.valid) {
                setCacheInvalid(false);
            } else {
                setCacheInvalid('invalid');
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('cache-config-changed', handleCacheChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('cache-config-changed', handleCacheChange);
        };
    }, []);

    // 💳 商业版：积分明细弹窗里的「充值」按钮通过事件打开充值弹窗
    useEffect(() => {
        if (IS_EDU) return;
        const handler = () => setShowRecharge(true);
        window.addEventListener('open-recharge', handler);
        return () => window.removeEventListener('open-recharge', handler);
    }, []);

    // 🎓 教育版：积分明细弹窗里的「兑换」按钮通过事件打开券码弹窗
    useEffect(() => {
        if (!IS_EDU) return;
        const handler = () => setShowVoucherRedeem(true);
        window.addEventListener('open-voucher-redeem', handler);
        return () => window.removeEventListener('open-voucher-redeem', handler);
    }, []);

    // �🎬 当 currentCrew 变化时，加载成员列表
    useEffect(() => {
        if (currentCrew?.id) {
            loadCrewMembers(currentCrew.id);
        }
    }, [currentCrew?.id]);

    // 🎬 点击外部关闭剧组下拉菜单
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (crewDropdownRef.current && !crewDropdownRef.current.contains(e.target)) {
                setShowCrewDropdown(false);
            }
        };
        if (showCrewDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCrewDropdown]);

    // 🖼️ 浮动海报垂直对齐：与网格卡片顶部对齐
    useEffect(() => {
        const updatePosterTop = () => {
            if (gridRef.current) {
                const rect = gridRef.current.getBoundingClientRect();
                setPosterTop(rect.top);
            }
        };
        updatePosterTop();
        window.addEventListener('scroll', updatePosterTop, { passive: true });
        window.addEventListener('resize', updatePosterTop, { passive: true });
        return () => {
            window.removeEventListener('scroll', updatePosterTop);
            window.removeEventListener('resize', updatePosterTop);
        };
    }, [loading, currentCrew]);

    useEffect(() => {
        if (editingId && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingId]);

    const handleCreateProject = async () => {
        if (creatingProject) return;
        setCreatingProject(true);
        try {
            const data = await api.post('/api/projects', { name: "未命名创意空间" }); // 🆕
            if (data.success && data.id) {
                navigate(`/editor/${data.id}`);
            }
        } catch(e) { console.error(e); }
        setCreatingProject(false);
    };

    const handleDeleteProject = async (e, id, name) => {
        e.stopPropagation();
        // 使用自定义确认弹窗
        setDeleteConfirm({ id, name });
    };
    
    const confirmDeleteProject = async () => {
        if (!deleteConfirm) return;
        try {
            await api.delete(`/api/projects/${deleteConfirm.id}`);
            setProjects(prev => prev.filter(p => p.id !== deleteConfirm.id));
            // 不删除本地缓存文件 — 文件在删除影片时按文件夹整体清理
        } catch(e) { console.error(e); }
        setDeleteConfirm(null);
    };

    const  startEditing = (e, p) => {
        e.stopPropagation();
        setEditingId(p.id);
        setEditName(p.name);
    };

    const saveProjectName = async (id) => {
        if (!editName.trim()) return setEditingId(null);
        const oldName = projects.find(p => p.id === id)?.name;
        if (editName === oldName) return setEditingId(null);

        try {
            await api.put(`/api/projects/${id}`, { name: editName }); // 🆕
            setProjects(prev => prev.map(p => p.id === id ? { ...p, name: editName } : p));
        } catch(e) { console.error(e); }
        setEditingId(null);
    };

    // 打开封面设置 - 加载项目图片
    const handleOpenCoverSetting = async (e, project) => {
        e.stopPropagation();
        setCoverProject(project);
        
        try {
            const data = await api.get(`/api/projects/${project.id}`);
            const nodes = data.nodes || [];
            
            // 筛选出所有图片节点
            const imageNodes = nodes.filter(n => n.data?.type === 'image' && n.data?.output);
            
            // 并行查询本地缓存（从三层缓存获取本地 blob URL）
            const imagePromises = imageNodes.map(async (n) => {
                try {
                    // 调用 getLocalUrlOrFetch 从三层缓存获取本地 URL
                    // L0: 内存 Map -> L1: IndexedDB -> L2: File System -> 远程下载并缓存
                    const result = await localCacheService.getLocalUrlOrFetch(
                        n.data.output,
                        currentCrew?.id,
                        currentCrew?.crew_name,
                        'image'
                    );
                    return {
                        url: result.url, // 优先使用本地缓存的 blob URL
                        title: n.data.title
                    };
                } catch (err) {
                    console.warn('获取本地缓存失败，回退到远程 URL:', n.data.output, err);
                    return {
                        url: n.data.output, // 回退到云存储 URL
                        title: n.data.title
                    };
                }
            });
            
            const images = await Promise.all(imagePromises);
            setProjectImages(images);
            setShowImagePicker(true);
        } catch (e) {
            console.error('加载项目图片失败:', e);
        }
    };

    // 选择图片后进入裁剪
    const handleSelectImage = (url) => {
        setShowImagePicker(false);
        setCropImageUrl(url);
    };

    // 裁剪完成后上传并更新
    const handleCoverSave = async (blob) => {
        if (!coverProject) return;
        
        const formData = new FormData();
        const fileName = `cover-${coverProject.id}-${Date.now()}.jpg`;
        formData.append('file', blob, fileName);
        
        try {
            // 上传图片到 thumbnails 目录
            const uploadData = await api.upload(`/api/upload?type=thumbnail`, formData); // 🆕
            
            if (uploadData.url) {
                // 更新项目封面
                await api.put(`/api/projects/${coverProject.id}`, { thumbnail: uploadData.url }); // 🆕
                
                // 更新本地状态（清除旧 localThumbUrl，触发缩略图缓存重新预热）
                setProjects(prev => prev.map(p =>
                    p.id === coverProject.id ? { ...p, thumbnail: uploadData.url, localThumbUrl: undefined } : p
                ));
            }
        } catch (e) {
            console.error('保存封面失败:', e);
        }
        
        // 关闭弹窗
        setCropImageUrl(null);
        setCoverProject(null);
    };

    // 关闭所有弹窗
    const handleCloseCoverModals = () => {
        setShowImagePicker(false);
        setCropImageUrl(null);
        setCoverProject(null);
        setProjectImages([]);
        setPosterMode(false);
    };

    /**
     * 🖼️ 打开「设置海报」流程（仅制片人可操作）
     *
     * 入口：仪表盘右侧浮动海报上的「更换海报」按钮
     * 流程：
     *   1. 标记 posterMode=true（让裁剪弹窗使用 9:16 比例 + 海报标题）
     *   2. 遍历当前影片所有舞台，收集所有图片节点的 URL
     *      - 优先从三层本地缓存获取 blob URL（避免跨域 + 更快）
     *      - 失败则回退到云存储远程 URL
     *   3. 打开图片选择器（setShowImagePicker）
     *   4. 用户选图 → handleSelectImage → setCropImageUrl → CoverCropperModal 裁剪
     *   5. 裁剪完成 → handlePosterSave 上传
     */
    const handleOpenPosterSetting = async () => {
        if (!currentCrew || currentCrew.role !== 'producer') return;
        setPosterMode(true);
        try {
            // 并行加载所有项目，从中收集图片节点（替代原来的串行 for 循环）
            const results = await Promise.allSettled(
                projects.map(async (p) => {
                    const data = await api.get(`/api/projects/${p.id}`);
                    const nodes = data.nodes || [];
                    return { project: p, imageNodes: nodes.filter(n => n.data?.type === 'image' && n.data?.output) };
                })
            );
            const allImages = [];
            for (const r of results) {
                if (r.status !== 'fulfilled') continue;
                const { project: p, imageNodes } = r.value;
                for (const n of imageNodes) {
                    try {
                        // 优先使用本地缓存 blob URL，避免云存储 CDN 跨域导致 canvas tainted
                        const result = await localCacheService.getLocalUrlOrFetch(n.data.output, currentCrew?.id, currentCrew?.name, 'image');
                        allImages.push({ url: result.url, title: `${p.name} - ${n.data.title || '图片'}` });
                    } catch {
                        allImages.push({ url: n.data.output, title: `${p.name} - ${n.data.title || '图片'}` });
                    }
                }
            }
            setProjectImages(allImages);
            setShowImagePicker(true);
        } catch (e) {
            console.error('加载海报候选图片失败:', e);
            setPosterMode(false);
        }
    };

    /**
     * 🖼️ 海报裁剪完成后上传并保存（仪表盘入口）
     *
     * @param {Blob} blob - CoverCropperModal 裁剪输出的 JPEG Blob（810×1440，9:16）
     *
     * 流程：
     *   1. 将 blob 上传到服务器 /api/upload?type=thumbnail（存到 /public/thumbnails/）
     *   2. PUT /api/crews/:id { poster: url } 更新数据库
     *   3. 同步更新 currentCrew 和 crews 本地 state（立即刷新浮动海报）
     *
     * ⚠️ 海报存储在本地服务器 /thumbnails/ 目录，不走云存储
     */
    const handlePosterSave = async (blob) => {
        if (!currentCrew) return;
        const formData = new FormData();
        const fileName = `poster-${currentCrew.id}-${Date.now()}.jpg`;
        formData.append('file', blob, fileName);
        try {
            const uploadData = await api.upload(`/api/upload?type=thumbnail`, formData);
            if (uploadData.url) {
                await api.put(`/api/crews/${currentCrew.id}`, { poster: uploadData.url });
                // 同步更新本地 state，浮动海报立即刷新，无需重新加载页面
                setCurrentCrew(prev => ({ ...prev, poster: uploadData.url }));
                setCrews(prev => prev.map(c =>
                    c.id === currentCrew.id ? { ...c, poster: uploadData.url } : c
                ));
            }
        } catch (e) {
            console.error('保存海报失败:', e);
        }
        setCropImageUrl(null);
        setPosterMode(false);
    };

    // 🗑️ 删除海报（仅制片人可操作，直接 PUT poster=null）
    const handleDeletePoster = async () => {
        if (!currentCrew) return;
        if (!window.confirm('确定要删除影片海报吗？')) return;
        try {
            await api.put(`/api/crews/${currentCrew.id}`, { poster: null });
            setCurrentCrew(prev => ({ ...prev, poster: null }));
            setCrews(prev => prev.map(c =>
                c.id === currentCrew.id ? { ...c, poster: null } : c
            ));
        } catch (e) {
            console.error('删除海报失败:', e);
        }
    };

    // 4个冷色调渐变方案（深色模式=原深蓝系）
    const COOL_GRADIENTS = [
        'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #3b5998 100%)', // 深蓝
        'linear-gradient(135deg, #2d3748 0%, #4a5568 50%, #5a6a7a 100%)', // 灰蓝
        'linear-gradient(135deg, #1a365d 0%, #2c5282 50%, #3182ce 100%)', // 海军蓝
        'linear-gradient(135deg, #2d3e50 0%, #34495e 50%, #4a6274 100%)', // 铁灰蓝
    ];

    // 浅色模式：浅蓝系（与深蓝一一对应，空白卡片不再压深）
    const COOL_GRADIENTS_LIGHT = [
        'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 50%, #93c5fd 100%)', // 浅蓝
        'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 50%, #b0bfd0 100%)', // 浅灰蓝
        'linear-gradient(135deg, #cfe8ff 0%, #a9d3f5 50%, #7fbce8 100%)', // 浅海军蓝
        'linear-gradient(135deg, #dde7f0 0%, #c3d4e3 50%, #a3bcd0 100%)', // 浅铁灰蓝
    ];

    // 根据项目 ID 获取对应的渐变色（按主题选深/浅蓝系）
    const getProjectGradient = (projectId) => {
        const arr = theme === 'light' ? COOL_GRADIENTS_LIGHT : COOL_GRADIENTS;
        return arr[projectId % arr.length];
    };

    return (
        <div 
            onClick={() => {
                if (editingCrewName) handleSaveCrewName();
            }}
            style={{ width: '100vw', height: '100vh', background: 'var(--bg-canvas)', color: 'var(--text-primary)', overflowY: 'auto', userSelect: 'none' }}
        >
            {/* 🎨 左下角深浅色切换按钮（与编辑器/登录页同款圆形） */}
            <button
                onClick={(e) => { e.stopPropagation(); toggleTheme(); }}
                title={theme === 'light' ? '浅色模式（点击切换深色）' : '深色模式（点击切换浅色）'}
                style={{
                    position: 'fixed', bottom: 20, left: 20, zIndex: 200,
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'var(--bg-panel)', border: '1px solid var(--border-subtle)',
                    boxShadow: 'var(--shadow-panel)', color: 'var(--text-secondary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-hover)'; e.currentTarget.style.borderColor = 'var(--text-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
            >
                {theme === 'light' ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
            </button>
            {/* 🆕 本地缓存横幅提示 */}
            {cacheInvalid && (
                <div style={{
                    position: 'sticky', top: 0, zIndex: 101,
                    background: cacheInvalid === 'invalid'
                        ? 'linear-gradient(135deg, rgba(127, 29, 29, 0.95), rgba(153, 27, 27, 0.95))'
                        : cacheInvalid === 'need-reauth'
                        ? 'linear-gradient(135deg, rgba(120, 53, 15, 0.95), rgba(146, 64, 14, 0.95))'
                        : 'linear-gradient(135deg, rgba(30, 58, 138, 0.95), rgba(29, 78, 216, 0.95))',
                    borderBottom: cacheInvalid === 'invalid'
                        ? '1px solid rgba(239, 68, 68, 0.5)'
                        : cacheInvalid === 'need-reauth'
                        ? '1px solid rgba(234, 179, 8, 0.5)'
                        : '1px solid rgba(96, 165, 250, 0.5)',
                    padding: '10px 32px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    backdropFilter: 'blur(8px)',
                    animation: 'slideDown 0.3s ease'
                }}>
                    <HardDrive size={16} color={cacheInvalid === 'invalid' ? '#fca5a5' : cacheInvalid === 'need-reauth' ? '#fde68a' : '#93c5fd'} />
                    <span style={{ color: cacheInvalid === 'invalid' ? '#fecaca' : cacheInvalid === 'need-reauth' ? '#fef9c3' : '#dbeafe', fontSize: 13 }}>
                        {cacheInvalid === 'invalid'
                            ? '本地缓存文件夹不可用（文件夹已删除），媒体文件将从服务器加载'
                            : cacheInvalid === 'need-reauth'
                            ? '本地缓存权限已过期，点击恢复即可继续使用加速缓存'
                            : '尚未配置本地缓存文件夹，配置后可加速媒体加载并支持离线访问'}
                    </span>
                    <button
                        onClick={cacheInvalid === 'need-reauth'
                            ? async () => {
                                setCacheReconfiguring(true);
                                const result = await localCacheService.reauthorizeCacheFolder();
                                setCacheReconfiguring(false);
                                if (result.success) { setCacheInvalid(false); }
                                else { setShowCacheConfirm(true); }
                              }
                            : () => setShowCacheConfirm(true)
                        }
                        disabled={cacheReconfiguring}
                        style={{
                            padding: '5px 14px', borderRadius: 6,
                            border: cacheInvalid === 'invalid'
                                ? '1px solid rgba(239, 68, 68, 0.6)'
                                : cacheInvalid === 'need-reauth'
                                ? '1px solid rgba(234, 179, 8, 0.6)'
                                : '1px solid rgba(96, 165, 250, 0.6)',
                            background: cacheInvalid === 'invalid'
                                ? 'rgba(239, 68, 68, 0.3)'
                                : cacheInvalid === 'need-reauth'
                                ? 'rgba(234, 179, 8, 0.3)'
                                : 'rgba(59, 130, 246, 0.3)',
                            color: cacheInvalid === 'invalid' ? '#fecaca' : cacheInvalid === 'need-reauth' ? '#fef9c3' : '#dbeafe',
                            fontSize: 12, fontWeight: 'bold',
                            cursor: cacheReconfiguring ? 'wait' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 4,
                            whiteSpace: 'nowrap', transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => { if (!cacheReconfiguring) e.currentTarget.style.background = cacheInvalid === 'invalid' ? 'rgba(239, 68, 68, 0.5)' : cacheInvalid === 'need-reauth' ? 'rgba(234, 179, 8, 0.5)' : 'rgba(59, 130, 246, 0.5)'; }}
                        onMouseLeave={e => { if (!cacheReconfiguring) e.currentTarget.style.background = cacheInvalid === 'invalid' ? 'rgba(239, 68, 68, 0.3)' : cacheInvalid === 'need-reauth' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(59, 130, 246, 0.3)'; }}
                    >
                        {cacheReconfiguring && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                        {cacheInvalid === 'invalid' ? '重新配置' : cacheInvalid === 'need-reauth' ? '恢复缓存' : '配置缓存'}
                    </button>
                    <button
                        onClick={() => setCacheInvalid(false)}
                        style={{
                            background: 'transparent', border: 'none',
                            color: cacheInvalid === 'invalid' ? '#fca5a5' : cacheInvalid === 'need-reauth' ? '#fde68a' : '#93c5fd',
                            cursor: 'pointer', padding: 4,
                            display: 'flex', alignItems: 'center', opacity: 0.7
                        }}
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Header - 固定在顶部 */}
            <div style={{ height: 120, borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', position: 'sticky', top: cacheInvalid ? 42 : 0, background: 'var(--bg-canvas)', zIndex: 100 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                    <div style={{width: 120}}><CineartLogo /></div>
                </div>
                
                {/* 🆕 右侧：剧组选择器 + 用户信息 + 设置按钮 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* 🎬 剧组选择器 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, color: 'var(--amber-accent)' }}>项目列表</span>
                        <div style={{ position: 'relative' }} ref={crewDropdownRef}>
                            <div 
                                onClick={() => setShowCrewDropdown(!showCrewDropdown)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '8px 12px', borderRadius: 8,
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid var(--border-subtle)',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    minWidth: 140
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                            >
                                <IconFilm size={16} color="var(--amber-accent)" />
                                <span style={{ fontSize: 13, color: 'var(--text-body)', flex: 1 }}>
                                    {currentCrew?.name || '选择项目'}
                                </span>
                                <ChevronDown size={14} style={{ color: '#71717a', transform: showCrewDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                            </div>
                            
                            {/* 剧组下拉菜单 */}
                            {showCrewDropdown && (
                                <div style={{
                                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                                    minWidth: '100%', width: 'max-content', // 宽度自适应 (至少和按钮一样宽，最长不限)
                                    background: 'var(--bg-panel)',
                                    border: '1px solid var(--border-subtle)',
                                    borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                                    overflow: 'hidden', zIndex: 1000
                                }}>
                                    {/* 剧组列表 - 高度自适应，不再限高 */}
                                    <div style={{}}>
                                        {crews.map(crew => (
                                            <div 
                                                key={crew.id}
                                                className="crew-item"
                                                style={{
                                                    padding: '10px 14px', cursor: 'pointer',
                                                    background: currentCrew?.id === crew.id ? 'rgba(59,130,246,0.2)' : 'transparent',
                                                    transition: 'background 0.15s',
                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                    position: 'relative'
                                                }}
                                                onMouseEnter={e => { if(currentCrew?.id !== crew.id) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                                onMouseLeave={e => { if(currentCrew?.id !== crew.id) e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                <div
                                                    onClick={() => handleSwitchCrew(crew)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: switchingCrewId ? 'wait' : 'pointer' }}
                                                >
                                                    {switchingCrewId === crew.id
                                                        ? <Loader2 size={14} style={{ color: 'var(--amber-accent)', flexShrink: 0, animation: 'spin 1s linear infinite' }} />
                                                        : <Film size={14} style={{ color: currentCrew?.id === crew.id ? 'var(--amber-accent)' : '#71717a', flexShrink: 0 }} />
                                                    }
                                                    <span style={{ flex: 1, fontSize: 13, fontWeight: currentCrew?.id === crew.id ? 600 : 400, color: currentCrew?.id === crew.id ? (theme === 'light' ? '#b45309' : '#e2e8f0') : (theme === 'light' ? '#3f3f46' : '#a1a1aa'), whiteSpace: 'nowrap' }}>
                                                        {crew.name}
                                                    </span>
                                                    {switchingCrewId === crew.id ? (
                                                        <span style={{ fontSize: 10, color: 'var(--amber-accent)', background: 'rgba(251,191,36,0.15)', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>切换中…</span>
                                                    ) : (<>
                                                    {crew.role === 'producer' && (
                                                        <span style={{ fontSize: 10, color: 'var(--amber-accent)', background: 'rgba(251,191,36,0.15)', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>我是制片人</span>
                                                    )}
                                                    {crew.role === 'director' && (
                                                        <span style={{ fontSize: 10, color: '#3b82f6', background: 'rgba(59,130,246,0.15)', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>我是导演</span>
                                                    )}
                                                    </>)}
                                                </div>
                                                {/* 🗑️ 删除按钮 - 仅制片人可见 */}
                                                {crew.role === 'producer' && crews.length > 1 && (
                                                    <QuickTooltip content="删除影片" position="left">
                                                        <button
                                                            onClick={(e) => handleDeleteCrew(e, crew)}
                                                            className="crew-delete-btn"
                                                            style={{
                                                                background: 'transparent',
                                                                border: 'none',
                                                                color: '#71717a',
                                                                padding: 4,
                                                                borderRadius: 4,
                                                                cursor: 'pointer',
                                                                opacity: 0,
                                                                transition: 'all 0.2s',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </QuickTooltip>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* 新建剧组按钮 */}
                                    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: 8 }}>
                                        <button
                                            onClick={handleCreateCrew}
                                            style={{
                                                width: '100%', padding: '8px 12px',
                                                background: 'rgba(59,130,246,0.1)',
                                                border: '1px solid rgba(59,130,246,0.2)',
                                                borderRadius: 6, color: '#3b82f6',
                                                fontSize: 13, cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; }}
                                        >
                                            <Plus size={14} />
                                            新建影片
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* 用户信息 - 点击打开个人设置 */}
                    <div 
                        onClick={() => setShowUserSettings(true)}
                        style={{ 
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 12px', borderRadius: 8,
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid var(--border-subtle)',
                            cursor: 'pointer', transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    >
                        {/* 头像 */}
                        <UserAvatar 
                            avatar={user?.avatar}
                            username={user?.username}
                            size={24}
                            fontSize={11}
                        />
                        <span style={{ fontSize: 13, color: 'var(--text-body)' }}>{user?.username}</span>
                        {(user?.role === 'super_admin' || user?.role === 'admin') && (
                            <span style={{ 
                                fontSize: 10, 
                                color: user?.role === 'super_admin' ? '#f59e0b' : '#3b82f6', 
                                background: user?.role === 'super_admin' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)', 
                                padding: '2px 6px', borderRadius: 4 
                            }}>{user?.role === 'super_admin' ? '系统管理员' : (user?.admin_title || '管理员')}</span>
                        )}
                        {/* 💰 积分显示（所有角色含超管均显示真实积分） */}
                        <QuickTooltip content="查看积分明细" position="bottom">
                            <span 
                                onClick={(e) => { e.stopPropagation(); setShowCreditLogs(true); }}
                                style={{ 
                                    fontSize: 11, color: 'var(--amber-accent)', 
                                    background: 'rgba(251,191,36,0.1)', 
                                    padding: '2px 8px', borderRadius: 4,
                                    display: 'flex', alignItems: 'center', gap: 3,
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(251,191,36,0.2)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(251,191,36,0.1)'}
                            >
                                <Sparkle size={13} color="var(--amber-accent)" strokeWidth={2.5} style={{ marginRight: 2 }} />
                                {Math.round(userCredits || 0)}
                            </span>
                        </QuickTooltip>
                        {/* 💳 按钮（所有角色含超管均可用，2026-06-16）；教育版无支付，整块删掉 */}
                        {!IS_EDU && (
                        <QuickTooltip content="积分" position="bottom">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowRecharge(true); }}
                                    style={{
                                        fontSize: 11, color: '#eab308',
                                        background: 'rgba(234,179,8,0.1)',
                                        padding: '3px 8px', borderRadius: 4,
                                        border: '1px solid rgba(234,179,8,0.2)',
                                        display: 'flex', alignItems: 'center', gap: 3,
                                        cursor: 'pointer', transition: 'all 0.2s',
                                        fontWeight: 500
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(234,179,8,0.2)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(234,179,8,0.1)'; }}
                                >
                                    <CreditCard size={12} />
                                    +
                                </button>
                            </QuickTooltip>
                        )}
                        {/* 🎓 教育版顶替入口：学生输老师发的券码换积分（免费，不涉及任何支付） */}
                        {IS_EDU && (
                            <QuickTooltip content="兑换算力券" position="bottom">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowVoucherRedeem(true); }}
                                    style={{
                                        fontSize: 11, color: '#34d399',
                                        background: 'rgba(16,185,129,0.1)',
                                        padding: '3px 8px', borderRadius: 4,
                                        border: '1px solid rgba(16,185,129,0.2)',
                                        display: 'flex', alignItems: 'center', gap: 3,
                                        cursor: 'pointer', transition: 'all 0.2s',
                                        fontWeight: 500
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.2)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.1)'; }}
                                >
                                    <Ticket size={12} />
                                    兑换
                                </button>
                            </QuickTooltip>
                        )}
                    </div>

                    {/* 🎬 制片人：团队积分管理按钮 */}
                    {currentCrew && currentCrew.role === 'producer' && (
                        <QuickTooltip content="团队积分管理" position="bottom">
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowCrewCreditManage(true); }}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 40, height: 40, borderRadius: 8,
                                    background: 'rgba(59,130,246,0.1)',
                                    border: '1px solid rgba(59,130,246,0.2)',
                                    color: '#3b82f6', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; }}
                            >
                                <Sparkle size={18} fill="#3b82f6" strokeWidth={0} />
                            </button>
                        </QuickTooltip>
                    )}

                    {/* 🆕 管理员：用户管理按钮 */}
                    {hasPermission('user_view') && (
                        <QuickTooltip content="用户管理" position="bottom">
                            <button 
                                onClick={() => setShowAdminPanel(true)}
                                style={{ 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 40, height: 40, borderRadius: 8,
                                    background: 'rgba(59,130,246,0.1)', 
                                    border: '1px solid rgba(59,130,246,0.2)',
                                    color: '#3b82f6', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; }}
                            >
                                <Users size={20} strokeWidth={1.5} />
                            </button>
                        </QuickTooltip>
                    )}

                    {/* 🎨 管理员：素材库管理按钮（环艺学姐专属） */}
                    {hasPermission('user_view') && IS_HUANSHE && (
                        <QuickTooltip content="素材库管理" position="bottom">
                            <button
                                onClick={() => setShowMaterialLibraryAdmin(true)}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 40, height: 40, borderRadius: 8,
                                    background: 'rgba(245,158,11,0.1)',
                                    border: '1px solid rgba(245,158,11,0.2)',
                                    color: '#f59e0b', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.2)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.1)'; }}
                            >
                                <Boxes size={20} strokeWidth={1.5} />
                            </button>
                        </QuickTooltip>
                    )}

                    {/* � 超级管理员：放号管理按钮 */}
                    {hasPermission('distribution') && (
                        <QuickTooltip content="放号管理" position="bottom">
                            <button 
                                onClick={() => setShowDistribution(true)}
                                style={{ 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 40, height: 40, borderRadius: 8,
                                    background: 'rgba(34,197,94,0.1)', 
                                    border: '1px solid rgba(34,197,94,0.2)',
                                    color: '#22c55e', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.2)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,197,94,0.1)'; }}
                            >
                                <Radio size={20} strokeWidth={1.5} />
                            </button>
                        </QuickTooltip>
                    )}
                    
                    {/* �🆕 管理员：邀请码管理按钮 */}
                    {hasPermission('invite_codes') && (
                        <QuickTooltip content="邀请码管理" position="bottom">
                            <button 
                                onClick={() => setShowInviteCode(true)}
                                style={{ 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 40, height: 40, borderRadius: 8,
                                    background: 'rgba(139,92,246,0.1)', 
                                    border: '1px solid rgba(139,92,246,0.2)',
                                    color: '#8b5cf6', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.2)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.1)'; }}
                            >
                                <Ticket size={20} strokeWidth={1.5} />
                            </button>
                        </QuickTooltip>
                    )}

                    {/* 🎓 教育版：算力券管理（老师批量生成券码发给学生）。
                        商业版走支付，process.env 折叠后整块被 terser 删掉。 */}
                    {IS_EDU && hasPermission('vouchers') && (
                        <QuickTooltip content="算力券管理" position="bottom">
                            <button
                                onClick={() => setShowVoucherPanel(true)}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 40, height: 40, borderRadius: 8,
                                    background: 'rgba(16,185,129,0.1)',
                                    border: '1px solid rgba(16,185,129,0.2)',
                                    color: '#10b981', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.2)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.1)'; }}
                            >
                                <Ticket size={20} strokeWidth={1.5} />
                            </button>
                        </QuickTooltip>
                    )}
                    
                    {/* 设置按钮 - 仅超级管理员可见 */}
                    {hasPermission('login_branding') && (
                            <QuickTooltip content="登录界面配置" position="bottom">
                                <button 
                                    onClick={() => setShowLoginBranding(true)}
                                    style={{ 
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: 40, height: 40, borderRadius: 8,
                                        background: 'rgba(255,255,255,0.05)', 
                                        border: '1px solid var(--border-subtle)',
                                        color: 'var(--text-secondary)', cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-hover)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                                >
                                    <Monitor size={20} strokeWidth={1.5} />
                                </button>
                            </QuickTooltip>
                            
                        )}
                        {hasPermission('ai_providers') && (
                            <QuickTooltip content="API 配置" position="bottom">
                                <button 
                                    onClick={() => setShowApiProvider(true)}
                                    style={{ 
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: 40, height: 40, borderRadius: 8,
                                        background: 'rgba(255,255,255,0.05)', 
                                        border: '1px solid var(--border-subtle)',
                                        color: 'var(--text-secondary)', cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.15)'; e.currentTarget.style.color = '#60a5fa'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                                >
                                    <Sparkle size={20} strokeWidth={1.5} />
                                </button>
                            </QuickTooltip>
                            
                        )}
                        {hasPermission('system_settings') && (
                            <QuickTooltip content="系统配置" position="bottom">
                                <button 
                                    onClick={() => setShowApiConfig(true)}
                                    style={{ 
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: 40, height: 40, borderRadius: 8,
                                        background: 'rgba(255,255,255,0.05)', 
                                        border: '1px solid var(--border-subtle)',
                                        color: 'var(--text-secondary)', cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-hover)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                                >
                                    <Settings size={20} strokeWidth={1.5} />
                                </button>
                            </QuickTooltip>
                    )}
                    
                    {/* 🆕 登出按钮 */}
                    <QuickTooltip content="退出登录" position="bottom">
                        <button 
                            onClick={logout}
                            style={{ 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 40, height: 40, borderRadius: 8,
                                background: 'rgba(255,255,255,0.05)', 
                                border: '1px solid var(--border-subtle)',
                                color: 'var(--text-secondary)', cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                        >
                            <LogOut size={20} strokeWidth={1.5} />
                        </button>
                    </QuickTooltip>
                </div>
            </div>
            
            {/* API 配置弹窗 */}
            {showApiConfig && <ApiConfigModal open={showApiConfig} onClose={() => setShowApiConfig(false)} />}
            {showApiProvider && <ApiProviderModal open={showApiProvider} onClose={() => setShowApiProvider(false)} />}
            
            {/* � 登录界面配置弹窗 */}
            {showLoginBranding && <LoginBrandingEditor open={showLoginBranding} onClose={() => setShowLoginBranding(false)} />}
            
            {/* �🆕 管理员面板 */}
            {showAdminPanel && <AdminPanel open={showAdminPanel} onClose={() => setShowAdminPanel(false)} />}

            {/* 🎨 素材库管理面板（环艺学姐） */}
            {showMaterialLibraryAdmin && <MaterialLibraryAdminPanel open={showMaterialLibraryAdmin} onClose={() => setShowMaterialLibraryAdmin(false)} />}
            
            {/* 🆕 邀请码管理弹窗 */}
            {showInviteCode && <InviteCodeModal open={showInviteCode} onClose={() => setShowInviteCode(false)} />}
            
            {/* 🎫 放号管理弹窗 */}
            {showDistribution && <DistributionPanel open={showDistribution} onClose={() => setShowDistribution(false)} />}
            
            {/* 🆕 用户设置弹窗 */}
            {showUserSettings && <UserSettingsModal 
                open={showUserSettings} 
                onClose={() => setShowUserSettings(false)} 
                onAvatarUpdated={() => currentCrew && loadCrewMembers(currentCrew.id)}
            />}
            
            {/* 🎬 剧组邀请弹窗 */}
            {showCrewInvite && currentCrew && (
                <CrewInviteModal 
                    open={showCrewInvite} 
                    onClose={() => setShowCrewInvite(false)} 
                    crewId={currentCrew.id}
                    crewName={currentCrew.name}
                />
            )}

            {/*
              🖼️ 浮动海报（Floating Poster）
              ─────────────────────────────────────────────────────────
              显示条件：当前影片有海报（crews.poster）且用户未手动关闭

              定位策略：position:fixed，挂在视口右侧空白区域
                - right: max(12px, calc((100vw - 1680px) / 4))
                  → 屏幕宽度 <= 1680px 时贴右边 12px；更宽则自动居中于内容区右侧空白
                - top: posterTop（由 useEffect 动态计算，与内容网格顶部对齐）

              尺寸：宽 240px，比例 9:16（竖屏电影海报标准比例）
              ⚠️ 此处 aspectRatio 必须与裁剪弹窗的 aspectRatio prop 保持一致（均为 9/16）

              操作按钮（仅制片人可见，hover 时显示）：
                - 删除海报 → handleDeletePoster（PUT poster=null）
                - 更换海报 → handleOpenPosterSetting（重新走图片选择+裁剪流程）
                - 关闭     → setPosterHidden(true)（仅隐藏，不删除数据，切换影片后重新显示）
            */}
            {currentCrew?.poster && !posterHidden && (
                <div
                    className="poster-card floating-poster"
                    style={{
                        position: 'fixed',
                        top: posterTop,
                        right: 'max(12px, calc((100vw - 1680px) / 4))',
                        width: 240,
                        aspectRatio: '9 / 16', // ⚠️ 改比例时同步改 EditorPage.js 和 DashboardPage.js 的裁剪 aspectRatio
                        zIndex: 50,
                        overflow: 'visible',
                        userSelect: 'none',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                        border: '1px solid var(--modal-input-border)',
                    }}
                >
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                        <ShimmerImg src={posterLocalUrl || currentCrew.poster} style={{
                            width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                            pointerEvents: 'none'
                        }} />
                    </div>
                    {/* 操作按钮 — hover显示，在海报框内右上角 */}
                    <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, zIndex: 3 }}>
                        {currentCrew?.role === 'producer' && (
                            <>
                                <QuickTooltip content="删除海报" position="top">
                                    <button
                                        onClick={handleDeletePoster}
                                        className="action-btn"
                                        style={{ background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer', opacity: 0, transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </QuickTooltip>
                                <QuickTooltip content="更换海报" position="top">
                                    <button
                                        onClick={handleOpenPosterSetting}
                                        className="action-btn"
                                        style={{ background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer', opacity: 0, transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
                                    >
                                        <Image size={14} />
                                    </button>
                                </QuickTooltip>
                            </>
                        )}
                        <QuickTooltip content="关闭" position="top">
                            <button
                                onClick={() => setPosterHidden(true)}
                                className="action-btn"
                                style={{ background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer', opacity: 0, transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
                            >
                                <X size={14} />
                            </button>
                        </QuickTooltip>
                    </div>
                </div>
            )}

            <div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        {/* 🎬 剧组名称（可编辑）- 使用原来"我的舞台"的样式 */}
                        {currentCrew && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <IconFilm size={26} color="var(--amber-accent)" />
                                {editingCrewName ? (
                                    <input
                                        ref={crewNameInputRef}
                                        value={crewNameInput}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setCrewNameInput(e.target.value)}
                                        onKeyDown={(e) => { 
                                            if(e.key === 'Enter') handleSaveCrewName(); 
                                            if(e.key === 'Escape') setEditingCrewName(false); 
                                        }}
                                        onBlur={handleSaveCrewName}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            borderBottom: '2px solid var(--amber-accent)',
                                            color: 'var(--amber-accent)', fontSize: 24, fontWeight: 'bold', outline: 'none',
                                            padding: '2px 0',
                                            width: Math.max(140, (crewNameInput || '').length * 28 + 20)
                                        }}
                                    />
                                ) : (
                                    <QuickTooltip content={currentCrew.role === 'producer' ? '点击编辑项目名称' : ''} position="top">
                                        <h1 
                                            style={{ 
                                                fontSize: 24, fontWeight: 'bold',
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                cursor: currentCrew.role === 'producer' ? 'pointer' : 'default'
                                            }}
                                            onClick={() => {
                                                if (currentCrew.role === 'producer') {
                                                    setCrewNameInput(currentCrew.name);
                                                    setEditingCrewName(true);
                                                }
                                            }}
                                        >
                                            <span style={{ color: 'var(--amber-accent)' }}>{currentCrew.name}</span>
                                            {currentCrew.role === 'producer' && <Pencil size={14} style={{ opacity: 0.6, color: 'var(--amber-accent)' }} />}
                                        </h1>
                                    </QuickTooltip>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* 🎬 剧组成员 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        {/* 成员头像列表 */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {crewMembers.slice(0, 5).map((member, idx) => (
                                <div 
                                    key={`member-${member.user_id}-${idx}`}
                                    style={{
                                        position: 'relative',
                                        marginLeft: idx === 0 ? 0 : -10,
                                        zIndex: 5 - idx
                                    }}
                                >
                                    {/* 头像 */}
                                    <QuickTooltip content={`${member.username}（${member.role === 'producer' ? '制片人' : '导演'}）`} position="top">
                                        <UserAvatar 
                                            avatar={member.avatar}
                                            username={member.username}
                                            size={40}
                                            fontSize={16}
                                            border={`2px solid ${member.role === 'producer' ? 'var(--amber-accent)' : '#3f3f46'}`}
                                        />
                                    </QuickTooltip>
                                </div>
                            ))}
                            {crewMembers.length > 5 && (
                                <div style={{
                                    width: 40, height: 40, borderRadius: '50%',
                                    background: '#27272a', border: '2px solid #3f3f46',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginLeft: -10, fontSize: 12, color: '#a1a1aa'
                                }}>
                                    +{crewMembers.length - 5}
                                </div>
                            )}
                        </div>
                        
                        {/* 邀请成员按钮（仅制片人可见）- 圆形加号 */}
                        {currentCrew?.role === 'producer' && (
                            <QuickTooltip content="邀请成员加入剧组" position="top">
                                <div
                                    onClick={() => setShowCrewInvite(true)}
                                    style={{
                                        width: 40, height: 40, borderRadius: '50%',
                                        background: 'rgba(59,130,246,0.1)',
                                        border: '2px dashed rgba(59,130,246,0.4)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => { 
                                        e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; 
                                        e.currentTarget.style.borderColor = 'rgba(59,130,246,0.6)';
                                    }}
                                    onMouseLeave={e => { 
                                        e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; 
                                        e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)';
                                    }}
                                >
                                    <Plus size={20} style={{ color: '#3b82f6' }} />
                                </div>
                            </QuickTooltip>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div style={{
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginTop: 120,
                        gap: 16
                    }}>
                        <Loader2 size={32} color="var(--text-muted)" style={{ animation: 'spin 1s linear infinite' }} />
                        <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>加载中...</span>
                    </div>
                ) : (
                    <div ref={gridRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>

                        {/* ➕ 新建舞台卡片 — 始终显示 */}
                        <div 
                            className="poster-card"
                            onClick={handleCreateProject}
                            style={{ 
                                position: 'relative',
                                background: 'var(--bg-panel)', 
                                borderRadius: 12, 
                                border: '2px dashed var(--border-strong)', 
                                overflow: 'visible', 
                                cursor: 'pointer', 
                                transition: 'all 0.25s ease',
                                minHeight: 240,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 12
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
                                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                                e.currentTarget.querySelector('.create-icon').style.background = 'rgba(59, 130, 246, 0.2)';
                                e.currentTarget.querySelector('.create-icon').style.color = '#3b82f6';
                                e.currentTarget.querySelector('.create-icon').style.transform = 'scale(1.1)';
                                e.currentTarget.querySelector('.create-text').style.color = '#3b82f6';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.borderColor = 'var(--border-strong)';
                                e.currentTarget.style.background = 'var(--bg-panel)';
                                e.currentTarget.querySelector('.create-icon').style.background = 'var(--bg-hover)';
                                e.currentTarget.querySelector('.create-icon').style.color = 'var(--text-muted)';
                                e.currentTarget.querySelector('.create-icon').style.transform = 'scale(1)';
                                e.currentTarget.querySelector('.create-text').style.color = 'var(--text-muted)';
                            }}
                        >
                            <div 
                                className="create-icon"
                                style={{ 
                                    width: 64, 
                                    height: 64, 
                                    borderRadius: '50%', 
                                    background: 'var(--bg-hover)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--text-muted)',
                                    transition: 'all 0.25s ease'
                                }}
                            >
                                {creatingProject
                                    ? <Loader2 size={28} strokeWidth={1.5} style={{ animation: 'spin 1s linear infinite' }} />
                                    : <Plus size={32} strokeWidth={1.5} />
                                }
                            </div>
                            <span 
                                className="create-text"
                                style={{ 
                                    fontSize: 14,
                                    color: 'var(--text-muted)',
                                    fontWeight: 500,
                                    transition: 'all 0.25s ease'
                                }}
                            >
                                {creatingProject ? '创建中...' : '新建图板'}
                            </span>
                        </div>
                        
                        {projects.map(p => (
                            <div
                                key={p.id}
                                className="project-card"
                                onClick={() => {
                                    setLoadingProjectId(p.id); // 立即显示加载状态
                                    // 使用 setTimeout 确保 UI 更新后再跳转
                                    setTimeout(() => navigate(`/editor/${p.id}`), 0);
                                }}
                                style={{
                                    background: 'var(--bg-panel)',
                                    borderRadius: 12,
                                    border: '1px solid var(--border-subtle)',
                                    overflow: 'visible',
                                    cursor: loadingProjectId === p.id ? 'wait' : 'pointer',
                                    transition: 'all 0.2s',
                                    position: 'relative',
                                    opacity: loadingProjectId === p.id ? 0.7 : 1,
                                    pointerEvents: loadingProjectId ? 'none' : 'auto' // 加载中禁止点击其他项目
                                }}
                                onMouseEnter={e => {
                                    if (loadingProjectId !== p.id) {
                                        e.currentTarget.style.borderColor = theme === 'light' ? 'rgba(63,63,70,0.55)' : 'rgba(255,255,255,0.3)';
                                    }
                                }}
                                onMouseLeave={e => {
                                    if (loadingProjectId !== p.id) {
                                        e.currentTarget.style.borderColor = 'var(--border-subtle)';
                                    }
                                }}
                            >
                                {/* 加载中遮罩 - z-index 必须足够高，确保覆盖所有内容 */}
                                {loadingProjectId === p.id && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'rgba(0,0,0,0.5)',
                                        borderRadius: 12,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 1000, // 提升 z-index 确保最前面
                                        backdropFilter: 'blur(4px)'
                                    }}>
                                        <div style={{ textAlign: 'center', color: '#fff' }}>
                                            <Loader2
                                                size={40}
                                                style={{
                                                    animation: 'spin 1s linear infinite',
                                                    marginBottom: 12,
                                                    color: '#3b82f6'
                                                }}
                                            />
                                            <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>打开中...</div>
                                        </div>
                                    </div>
                                )}
                                <div style={{ height: 160, background: '#27272a', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
                                    {p.thumbnail ? (
                                        <ShimmerImg src={p.localThumbUrl || p.thumbnail} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            background: getProjectGradient(p.id),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: theme === 'light' ? 'rgba(30,58,95,0.35)' : 'rgba(255,255,255,0.3)',
                                        }}>
                                            <LayoutGrid size={48} strokeWidth={1} />
                                        </div>
                                    )}
                                </div>
                                <div style={{ padding: 16 }}>
                                    {editingId === p.id ? (
                                        <input 
                                            ref={inputRef}
                                            value={editName}
                                            onChange={e => setEditName(e.target.value)}
                                            onBlur={() => saveProjectName(p.id)}
                                            onKeyDown={e => { if(e.key === 'Enter') saveProjectName(p.id); }}
                                            onClick={e => e.stopPropagation()}
                                            style={{ width: '160px', background: '#000', border: '1px solid #3b82f6', color: '#fff', padding: '4px 8px', borderRadius: 4, outline: 'none' }}
                                        />
                                    ) : (
                                        <div style={{ fontWeight: 'bold', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>{p.name}</span>
                                            <QuickTooltip content="重命名" position="top">
                                                <button 
                                                    onClick={(e) => startEditing(e, p)}
                                                    style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                                                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-hover)'}
                                                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                                                >
                                                    <Edit2 size={12} />
                                                </button>
                                            </QuickTooltip>
                                        </div>
                                    )}
                                    <div style={{ fontSize: 12, color: '#71717a', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Plus size={12} /> 创建: {new Date(p.created_at).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Clock size={12} /> 修改: {new Date(p.updated_at).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
                                    <QuickTooltip content="设置封面" position="top">
                                        <button 
                                            onClick={(e) => handleOpenCoverSetting(e, p)}
                                            className="action-btn"
                                            style={{ background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer', opacity: 0, transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <Image size={14} />
                                        </button>
                                    </QuickTooltip>
                                    <QuickTooltip content="删除" position="top">
                                        <button 
                                            onClick={(e) => handleDeleteProject(e, p.id, p.name)}
                                            className="action-btn del-btn"
                                            style={{ background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', padding: 6, borderRadius: 6, cursor: 'pointer', opacity: 0, transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </QuickTooltip>
                                </div>
                                
                                {/* 🆕 项目状态指示器 - 卡片右下角 */}
                                <ProjectStatusIndicator 
                                    pendingTasks={p.pendingTasks} 
                                    hasUnseenCompleted={p.hasUnseenCompleted} 
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <style>{`
                .project-card:hover .action-btn, .poster-card:hover .action-btn { opacity: 1 !important; pointer-events: auto; }
                .action-btn { opacity: 0; pointer-events: none; }
                .action-btn:hover { background: #3b82f6 !important; }
                .del-btn:hover { background: #ef4444 !important; }
                .crew-item:hover .crew-delete-btn { opacity: 1 !important; }
                .crew-delete-btn:hover { background: rgba(239,68,68,0.2) !important; color: #ef4444 !important; }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .floating-poster:hover .action-btn { opacity: 1 !important; pointer-events: auto; }
                @keyframes slideDown {
                    from { transform: translateY(-100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>

            {/* 图片选择弹窗 */}
            {showImagePicker && (
                <ImagePickerModal
                    images={projectImages}
                    onSelect={handleSelectImage}
                    onClose={handleCloseCoverModals}
                />
            )}

            {/* 封面/海报裁剪弹窗 */}
            {cropImageUrl && (
                <CoverCropperModal
                    imageUrl={cropImageUrl}
                    onClose={handleCloseCoverModals}
                    onSave={posterMode ? handlePosterSave : handleCoverSave}
                    {...(posterMode ? { aspectRatio: 9/16, title: '设置影片海报', buttonText: '设为海报' } : {})}
                />
            )}

            {/* 删除确认弹窗 */}
            {deleteConfirm && (
                <ConfirmModal
                    title="删除舞台"
                    message={`确定要删除舞台「${deleteConfirm.name}」吗？此操作无法撤销。`}
                    confirmText="删除"
                    cancelText="取消"
                    danger={true}
                    onConfirm={confirmDeleteProject}
                    onCancel={() => setDeleteConfirm(null)}
                />
            )}

            {/* 🎬 删除影片确认弹窗 */}
            {deleteCrewConfirm && (
                <ConfirmModal
                    title="删除影片"
                    message={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ fontSize: 14, color: 'var(--text-body)' }}>
                                确定要删除影片「{deleteCrewConfirm.name}」吗？
                            </div>
                            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: 12 }}>
                                <div style={{ fontSize: 13, color: '#fca5a5', fontWeight: 600, marginBottom: 8 }}>
                                    ⚠️ 警告：此操作将永久删除以下内容
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#f87171', lineHeight: 1.8 }}>
                                    <li>{deleteCrewConfirm.projectCount} 个舞台及其所有内容</li>
                                    <li>该影片的公共素材库</li>
                                    <li>所有舞台的私有素材库</li>
                                    {deleteCrewConfirm.memberCount > 1 && (
                                        <li>将影响 {deleteCrewConfirm.memberCount} 名成员（包括您自己）</li>
                                    )}
                                </ul>
                            </div>
                            <div style={{ fontSize: 12, color: '#a1a1aa', fontStyle: 'italic' }}>
                                此操作无法撤销，请谨慎操作！
                            </div>
                        </div>
                    }
                    confirmText="确认删除"
                    cancelText="取消"
                    danger={true}
                    onConfirm={confirmDeleteCrew}
                    onCancel={() => setDeleteCrewConfirm(null)}
                />
            )}

            {/* 🆕 缓存配置确认弹窗 */}
            {showCacheConfirm && (
                <ConfirmModal
                    title={cacheInvalid === 'invalid' ? "重新配置本地缓存" : "配置本地缓存"}
                    message={cacheInvalid === 'invalid'
                        ? "浏览器将请求文件夹读写权限（正常安全机制）。请选择一个本地文件夹用于缓存媒体文件。"
                        : "选择一个本地文件夹用于缓存媒体文件，可大幅加速加载速度并支持离线查看。浏览器将请求文件夹读写权限（正常安全机制）。"}
                    confirmText="选择文件夹"
                    cancelText="取消"
                    onConfirm={async () => {
                        setShowCacheConfirm(false);
                        setCacheReconfiguring(true);
                        const result = await localCacheService.requestCacheFolder();
                        setCacheReconfiguring(false);
                        if (result.success) {
                            setCacheInvalid(false);
                        }
                    }}
                    onCancel={() => setShowCacheConfirm(false)}
                />
            )}

            {/* 积分明细弹窗 */}
            <CreditLogsModal
                open={showCreditLogs}
                onClose={() => setShowCreditLogs(false)}
            />

            {/* 🎬 剧组积分管理弹窗 */}
            {currentCrew && (
                <CrewCreditManageModal
                    open={showCrewCreditManage}
                    onClose={() => setShowCrewCreditManage(false)}
                    crewId={currentCrew.id}
                    producerCredits={userCredits}
                    onCreditsChange={() => {
                        // 积分变化后刷新
                        api.get('/api/user/credits').then(data => {
                            setUserCredits(data.credits || 0);
                        }).catch(err => console.error('刷新积分失败:', err));
                    }}
                />
            )}

            {/* 💳 弹窗 */}
            <RechargeModal
                open={showRecharge}
                onClose={() => setShowRecharge(false)}
                onSuccess={(credits) => {
                    // 成功后刷新积分
                    setUserCredits(prev => prev + credits);
                }}
            />

            {/* 🎓 算力券（教育版）。process.env 是构建期常量，商业版这两块整个消失 */}
            {IS_EDU && (
                <VoucherRedeemModal
                    open={showVoucherRedeem}
                    onClose={() => setShowVoucherRedeem(false)}
                    onSuccess={(balance) => setUserCredits(balance)}
                />
            )}
            {IS_EDU && showVoucherPanel && (
                <VoucherPanel open={showVoucherPanel} onClose={() => setShowVoucherPanel(false)} />
            )}
        </div>
    );
}
